<template>
  <div class="pa-4">
    <div v-if="loading">
      <v-skeleton-loader type="article">
      </v-skeleton-loader>
    </div>
    <div v-else-if="!loading && academyClass">
      <v-card elevation="0">
        <v-card-text class="pb-0 mb-0 mt-0 pt-0">
          <v-switch v-if="!academyClass.vacation" dense :label="$t('message.disabled')"
            v-model="academyClass.disabled"></v-switch>
          <v-switch :disabled="true" v-if="academyClass.vacation" dense :label="$t('message.vacation')"
            v-model="academyClass.vacation"></v-switch>
        </v-card-text>
        <v-card-title>{{
          $d(new Date(academyClass.date), "month_day_year", $i18n.locale)
          }}</v-card-title>
        <v-card-text>
          <v-text-field outlined dense width="100%" v-model="academyClass.title"
            :label="$t('message.title')"></v-text-field>
        </v-card-text>
        <div v-if="!academyClass.disabled">
          <v-card-text>
            <members-autocomplete :label="$t('message.professor')" :admins="true" v-model="selectedProfessor" />
            <v-textarea v-if="!academyClass.vacation" outlined width="100%" v-model="academyClass.notes"
              :label="$t('message.notes')"></v-textarea>
            <v-text-field v-if="!academyClass.vacation" outlined dense v-model="academyClass.maxCapacity"
              :label="$t('message.max_capacity')"></v-text-field>
            <v-text-field v-if="!academyClass.vacation" outlined dense v-model="academyClass.duration"
              :label="$t('message.duration') + ' (' + $t('message.minutes') + ')'"></v-text-field>
            <v-autocomplete v-if="!academyClass.vacation && videos && videos.length > 0" :label="$t('message.video')"
              :items="videos" item-text="title" item-value="_id" v-model="academyClass.video" clearable outlined
              dense></v-autocomplete>
            <v-switch hide-details v-if="academyClass.maxCapacity > 0" v-model="academyClass.allowWaitlist"
              :label="$t('message.allow_waitlist')"></v-switch>
            <div class="text-caption">{{ $t('message.allow_waitlist_explained') }}</div>
            <v-switch hide-details v-if="!academyClass.vacation" v-model="academyClass.allowNonMembers"
              :label="$t('message.allow_non_members_join')"></v-switch>
            <div class="text-caption">{{ $t('message.allow_non_members_join_explained') }}</div>
            <v-switch hide-details v-if="!academyClass.vacation" v-model="academyClass.skipApproval"
              :label="$t('message.skip_approval')"></v-switch>
            <div class="text-caption">{{ $t('message.skip_approval_explained') }}</div>
          </v-card-text>
          <v-card-text v-if="!academyClass.vacation">
            <div class="text-subtitle-2">{{ $t("message.date") }}:</div>
            <DatePicker :show-time="true" v-model="academyClass.date" :past-event="false">
            </DatePicker>
          </v-card-text>
          <v-card-text v-else>
            <div class="text-subtitle-2">{{ $t("message.start_date") }}:</div>
            <DatePicker :show-time="true" v-model="academyClass.date" :past-event="false">
            </DatePicker>
            <div class="text-subtitle-2">{{ $t("message.end_date") }}:</div>
            <DatePicker :show-time="true" v-model="academyClass.endDate" :past-event="false">
            </DatePicker>
          </v-card-text>
          <v-card-text v-if="displayAtendeesForm && !academyClass.vacation">
            <v-card class="mx-auto" elevation="0" outlined>
              <v-card-title class="text-subtitle-1">
                {{ $t("message.atendees") }} -
                {{ totalAttendees }}
                <span v-if="academyClass.maxCapacity > 0">
                  /{{ academyClass.maxCapacity }}
                </span>
              </v-card-title>
              <v-card-text v-if="!loadingAttendees" class="pa-0">
                <v-list v-if="totalAttendees > 0">
                  <v-list-group v-if="academyClass.attendees && academyClass.attendees.length && !loadingAttendees"
                    :value="attendeesActivator">
                    <template v-slot:activator>
                      <v-list-item-title>
                        <v-icon>mdi-map-marker-check-outline</v-icon>
                        {{ $t("message.attendees") }} ({{
                          academyClass.attendees
                            ? academyClass.attendees.length
                            : 0
                        }})
                      </v-list-item-title>
                    </template>
                    <v-list-item v-for="(checkin, i) in academyClass.attendees" :key="'attendee_' + i" link
                      @click="goToMember(checkin.user._id, checkin.user.displayName)">
                      <v-list-item-avatar v-if="checkin.user">
                        <ImageDisplay v-if="checkin.user.userImageSmall" :src="checkin.user.userImageSmall" />
                        <img v-else :src="assets('default-profile-avatar.png')" />
                      </v-list-item-avatar>
                      <v-list-item-avatar v-else-if="checkin.visitorData">
                        <v-icon color="accent">mdi-target-account</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-action class="mr-1">
                        <v-icon title="Tablet" v-if="checkin.source == 'tablet'">mdi-tablet</v-icon>
                        <v-icon title="Manual" v-else-if="checkin.source == 'manual'">mdi-monitor</v-icon>
                        <v-icon title="App" v-else>mdi-cellphone</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title v-if="checkin.user" v-text="checkin.user.displayName"></v-list-item-title>
                        <v-list-item-title v-else-if="checkin.visitorData"
                          v-text="checkin.visitorData.name"></v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $d(new Date(checkin.date), "time", $i18n.locale) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-group>
                  <v-list-group v-if="academyClass.approved && academyClass.approved.length && !loadingAttendees"
                    :value="approvedActivator">
                    <template v-slot:activator>
                      <v-list-item-title>
                        <v-icon>mdi-check-circle-outline</v-icon>
                        {{ $t("message.approved") }} ({{
                          academyClass.approved
                            ? academyClass.approved.length
                            : 0
                        }})
                      </v-list-item-title>
                    </template>
                    <v-list-item v-for="(user, i) in academyClass.approved" :key="'approved_' + i" link
                      @click="goToMember(user._id, user.displayName)">
                      <v-list-item-avatar>
                        <ImageDisplay v-if="user && user.userImageSmall" :src="user.userImageSmall" />
                        <img v-else :src="assets('default-profile-avatar.png')" />
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ user.displayName }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <LocationMarkerIcon v-if="user.checkedIn" size="15" :color="themeColor('success')">
                        </LocationMarkerIcon>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list-group>
                  <v-list-group v-if="academyClass.pending && academyClass.pending.length && !loadingAttendees"
                    :value="pendingActivator">
                    <template v-slot:activator>
                      <v-list-item-title>
                        <v-icon>mdi-clock-outline</v-icon>
                        {{ $t("message.pending") }} ({{
                          academyClass.pending ? academyClass.pending.length : 0
                        }})
                      </v-list-item-title>
                    </template>
                    <div v-if="academyClass.pending">
                      <v-list-item v-for="(user, i) in academyClass.pending" :key="'pending_' + i" link
                        @click="goToMember(user._id, user.displayName)">
                        <v-list-item-avatar>
                          <ImageDisplay v-if="user.userImageSmall" :src="user.userImageSmall" />
                          <img v-else :src="assets('default-profile-avatar.png')" />
                        </v-list-item-avatar>

                        <v-list-item-content>
                          <v-list-item-title v-text="user.displayName"></v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-progress-circular v-if="approvingId === user._id" indeterminate size="20" width="2" color="success"></v-progress-circular>
                          <v-icon v-else @click.stop="approveRequest(user._id)" color="success">mdi-check-circle-outline</v-icon>
                          <v-icon @click.stop="rejectRequest(user._id)">mdi-close-circle-outline</v-icon>
                        </v-list-item-action>
                      </v-list-item>
                    </div>
                    <v-list-item v-else>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t("message.no_data") }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-group>
                  <v-list-group v-if="academyClass.visitors && academyClass.visitors.length && !loadingAttendees"
                    :value="visitorsActivator">
                    <template v-slot:activator>
                      <v-list-item-title>
                        <v-icon>mdi-target-account</v-icon>
                        {{ $t("message.visitors") }} ({{
                          academyClass.visitors ? academyClass.visitors.length : 0
                        }})
                      </v-list-item-title>
                    </template>
                    <v-list-item v-for="(user, i) in academyClass.visitors" :key="'pending_' + i" link
                      @click="goToVisitor(user._id)">
                      <v-list-item-avatar>
                        <img :src="assets('default-profile-avatar.png')" />
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title v-text="user.name"></v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn @click.stop="removeVisitor(user)" icon
                          small><v-icon>mdi-trash-can-outline</v-icon></v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list-group>
                </v-list>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn outlined color="primary" elevation="0" small @click="inviteMemberDialog = true">
                    {{ $t("message.add") }}
                  </v-btn>
                </v-card-actions>
              </v-card-text>
              <v-card-text v-else class="text-center">
                <circles-loader></circles-loader>
              </v-card-text>
            </v-card>
          </v-card-text>
        </div>
        <div v-else-if="academyClass">
          <v-card-text>
            <div class="text-subtitle-2">{{ $t("message.start_date") }}:</div>
            {{ $d(new Date(academyClass.date), "long", $i18n.locale) }}
          </v-card-text>
          <v-list-group v-if="academyClass.visitors && academyClass.visitors.length && !loadingAttendees"
            :value="visitorsActivator">
            <template v-slot:activator>
              <v-list-item-title>
                <v-icon>mdi-target-account</v-icon>
                {{ $t("message.visitors") }} ({{
                  academyClass.visitors ? academyClass.visitors.length : 0
                }})
              </v-list-item-title>
            </template>
            <v-list-item v-for="(user, i) in academyClass.visitors" :key="'pending_' + i" link
              @click="goToVisitor(user._id)">
              <v-list-item-avatar>
                <img :src="assets('default-profile-avatar.png')" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title v-text="user.name"></v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn @click.stop="removeVisitor(user)" icon small><v-icon>mdi-trash-can-outline</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list-group>
        </div>
        <v-card-actions>
          <v-btn outlined elevation="0" small color="primary" @click.stop="editEvent()">{{
            $t("message.update")
            }}</v-btn>
          <v-btn elevation="0" small @click.stop="goBack()">{{
            $t("message.back")
            }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="0" small color="error" @click.stop="removeEvent()">{{
            $t("message.delete")
            }}</v-btn>
        </v-card-actions>
        <v-card-actions>
          <v-btn outlined elevation="0" small v-if="bulkAdded > 1" color="primary" @click.stop="editEvent(true)">{{
            $t("message.bulk_update") + ' (' + bulkAdded + ')'
            }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="0" small v-if="bulkAdded > 1" color="error" @click.stop="removeEvent(true)">{{
            $t("message.bulk_delete") + ' (' + bulkAdded + ')'
            }}</v-btn>
        </v-card-actions>
      </v-card>
      <v-dialog v-model="inviteMemberDialog" width="80%">
        <v-card outlined elevation="0">
          <v-card-title>{{ $t("message.add") }}</v-card-title>
          <v-card-text>
            <members-autocomplete v-show="visitorToInvite ? false : true" :label="$t('message.member')" :admins="false"
              v-model="memberToInvite" />
            <VisitorsAutocomplete v-show="memberToInvite ? false : true" :label="$t('message.add_visitor')"
              v-model="visitorToInvite" />
          </v-card-text>
          <v-card-actions>
            <v-btn outlined @click="inviteMember()" small elevation="0" color="primary">
              {{ $t("message.add") }}
            </v-btn>
            <v-btn small elevation="0"
              @click="inviteMemberDialog = false; memberToInvite = null; visitorToInvite = null;">
              {{ $t("message.cancel") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import MembersAutocomplete from "@/components/inputs/MembersAutocomplete.vue";
import VisitorsAutocomplete from "@/components/inputs/VisitorsAutocomplete.vue";
import Api from "@/services/Api2";

export default {
  data() {
    return {
      loading: true,
      academyClassId: this.$route.params.academyClassId,
      academyClass: null,
      memberToInvite: null,
      visitorToInvite: null,
      approvedActivator: true,
      attendeesActivator: true,
      pendingActivator: true,
      visitorsActivator: true,
      loadingAttendees: true,
      approvingId: null,
      videos: null,
      totalAttendees: 0,
      displayAtendeesForm: false,
      selectedProfessor: null,
      bulkAdded: null,
      timezoneOffset: null,
      inviteMemberDialog: null,
      allowedMinutes: [0, 15, 30, 45],
    };
  },
  components: {
    CirclesLoader,
    MembersAutocomplete,
    VisitorsAutocomplete,
    DatePicker,
  },
  created() {
    this.loading = true;
    this.getClass();
    this.getVideos();
  },
  watch: {},
  methods: {
    removeVisitor(i) {
      Api()
        .delete(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/classes/" +
          this.academyClassId + "/visitor/remove/" + i._id
        )
        .then(() => {
          this.getClass();
          this.showAlert("success", this.$t("message.success"));
        }).catch(() => {
          this.showAlert(
            "error",
            this.$t("message.error_generic")
          );
        });
    },
    inviteMember() {
      let id = this.memberToInvite;
      if (!id)
        id = this.visitorToInvite + '?visitor=true';
      Api()
        .patch(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/classes/" +
          this.academyClassId +
          "/add/" +
          id
        )
        .then((response) => {
          if (response.data) {
            this.showAlert("success", this.$t("message.success"));
            this.memberToInvite = null;
            this.visitorToInvite = null;
            this.inviteMemberDialog = null;
            this.getClass();
          }
        }).catch(() => {
          this.showAlert(
            "error",
            this.$t("message.error_generic")
          );
        });
    },
    getVideos() {
      Api()
        .get("videos/admins/" + this.$store.state.managedAcademy + '/titles/all')
        .then(response => {
          const data = response.data;
          if (data.videos && data.videos.length > 0) {
            this.videos = data.videos;
          }
        });
    },
    goToVisitor(id) {
      this.navigateTo({
        name: "viewvisitor",
        params: { visitorId: id },
      });
    },
    getClass() {
      Api()
        .get(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/classes/" +
          this.academyClassId
        )
        .then((response) => {
          let academyClass = response.data.academyClass;
          academyClass.date = this.convertUtcToZonedDate(academyClass.date, response.data.timeZone);
          //academyClass.date = this.parseLocalDate(academyClass.date);
          if (academyClass.startNumber) {
            const t = this.numberToHoursAndMinutes(academyClass.startNumber);
            academyClass.date.setHours(t.hours);
            academyClass.date.setMinutes(t.minutes);
          }
          if (academyClass.endDate)
            academyClass.endDate = this.convertUtcToZonedDate(academyClass.endDate, response.data.timeZone);

          this.academyClass = academyClass;

          const attendees = [];
          if (response.data.bulkAdded)
            this.bulkAdded = response.data.bulkAdded;
          if (response.data.attendees && response.data.attendees.length > 0) {
            for (const a of response.data.attendees) {
              let found = false;
              for (let approvedUser of this.academyClass.approved) {
                if (a.user._id == approvedUser._id)
                  found = true;
              }
              if (!found)
                a.approved = false;
              else
                a.approved = true;
              attendees.push(a);
            }
          }
          const visitors = academyClass.visitors ? academyClass.visitors.length : 0;
          const approved = academyClass.approved ? academyClass.approved.length : 0;
          const pending = academyClass.pending ? academyClass.pending.length : 0;
          this.totalAttendees = visitors + approved + pending;
          this.academyClass.attendees = attendees;
          if (this.academyClass.attendees && this.academyClass.attendees.length > 0) {
            //Approved and atteendees, lets add some extra info
            for (let checkedinUser of this.academyClass.attendees) {
              for (let approvedUser of this.academyClass.approved) {
                if (checkedinUser.user._id == approvedUser._id) {
                  approvedUser.checkedIn = true;
                  checkedinUser.approved = true;
                  break;
                }
              }
              if (!checkedinUser.approved) {
                this.totalAttendees++;
              }
            }
          }
          if (!this.academyClass.approved)
            this.academyClass.approved = [];
          if (!this.academyClass.pending)
            this.academyClass.pending = [];
          if (this.academyClass.professorUser)
            this.selectedProfessor = this.academyClass.professorUser._id;
          this.showAttendeesForm();
          this.loading = false;
          this.loadingAttendees = false;
        });
    },
    approveRequest(id) {
      if (this.approvingId) return;
      this.approvingId = id;
      Api()
        .patch(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/classes/" +
          this.academyClassId +
          "/accept/" +
          id
        )
        .then((response) => {
          if (response.data) {
            this.showAlert("success", this.$t("message.success"));
            this.getClass();
          }
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error"));
        })
        .finally(() => {
          this.approvingId = null;
        });
    },
    rejectRequest(id) {
      Api()
        .patch(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/classes/" +
          this.academyClassId +
          "/reject/" +
          id
        )
        .then((response) => {
          if (response.data) {
            this.showAlert("success", this.$t("message.success"));
            this.getClass();
          }
        });
    },
    showAttendeesForm() {
      this.displayAtendeesForm = true;
    },
    getStartNumber() {
      if (this.academyClass.date) {
        const d = new Date(this.academyClass.date);
        const hours = d.getHours();
        const minutes = d.getMinutes();
        return parseFloat(hours + "." + String(minutes).padStart(2, "0"));
      }
    },
    editEvent(bulk) {
      this.displayAtendeesForm = false;
      if (
        this.academyClass.maxCapacity &&
        this.academyClass.maxCapacity != "" &&
        isNaN(this.academyClass.maxCapacity)
      ) {
        this.showAlert("error", this.$t("message.max_capacity_nan"));
        return;
      }
      this.academyClass.startNumber = this.getStartNumber();

      if (
        this.academyClass.duration &&
        this.academyClass.duration != "" &&
        isNaN(this.academyClass.duration)
      ) {
        this.academyClass.duration = null;
      }
      //Revert time to normal in case we are on a different timezone
      const adjustedDate = this.adjustTimeZone(new Date(this.academyClass.date), this.timezoneOffset, true);
      const data = {
        title: this.academyClass.title,
        skipApproval: this.academyClass.skipApproval,
        professorUser: this.selectedProfessor,
        notes: this.academyClass.notes,
        maxCapacity: this.academyClass.maxCapacity,
        disabled: this.academyClass.disabled,
        duration: this.academyClass.duration,
        date: adjustedDate,
        bulkUpdate: bulk,
        allowWaitlist: this.academyClass.allowWaitlist,
        allowNonMembers: this.academyClass.allowNonMembers,
        video: this.academyClass.video,
        startNumber: this.academyClass.startNumber,
      };
      Api()
        .patch(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/event/" +
          this.academyClassId,
          data
        )
        .then((response) => {
          if (response) {
            this.showAttendeesForm();
            this.showAlert("success", this.$t("message.success"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    removeEvent(bulk) {
      Api()
        .delete(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/event/" +
          this.academyClassId + (bulk ? '?bulk=true' : '')
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.goBack();
          }
        });
    },
  },
};
</script>

<style scoped></style>
